import { Form, Link, useLoaderData, type LoaderFunctionArgs } from 'react-router';
import { CONTACT_EVENT_TYPES } from '@/types';
import { api, toQuery } from '@/lib/api';
import { Pagination } from '@/components/pagination';
import { RowLink } from '@/components/row-link';
import { fmtDateTime, displayName } from '@/lib/format';

type Event = {
  id: string;
  type: string;
  source: string;
  occurredAt: string;
  payload: Record<string, unknown> | null;
  contact: { id: string; email: string; firstName: string | null; lastName: string | null; tier: string } | null;
};

export async function auditLoader({ request }: LoaderFunctionArgs) {
  const sp = new URL(request.url).searchParams;
  const query = { type: sp.get('type') ?? undefined, page: Number(sp.get('page') ?? 1), pageSize: 50 };
  const data = await api<{ items: Event[]; total: number; page: number; pageSize: number }>(`/audit${toQuery(query)}`);
  return { query, data };
}

export function AuditPage() {
  const { query, data } = useLoaderData<typeof auditLoader>();

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl">Audit log</h1>
        <p className="text-sm text-muted-foreground">Every account change detected by the sync, every email event, every manual CRM action.</p>
      </div>
      <Form key={query.type ?? ''} className="flex items-end gap-3 rounded-lg border bg-white p-3 text-sm" method="get">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Event type</span>
          <select name="type" defaultValue={query.type ?? ''} className="h-9 rounded-md border px-2">
            <option value="">Any</option>
            {CONTACT_EVENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <button className="h-9 rounded-md bg-[var(--ff-primary)] px-4 text-white">Filter</button>
      </Form>
      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="data-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Event</th>
              <th>Contact</th>
              <th>Source</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && (
              <tr>
                <td colSpan={5} className="py-8 text-center text-muted-foreground">
                  No events yet
                </td>
              </tr>
            )}
            {data.items.map((e) => (
              <RowLink key={e.id} href={e.contact ? `/contacts/${e.contact.id}` : '#'}>
                <td className="whitespace-nowrap">{fmtDateTime(e.occurredAt)}</td>
                <td>
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs">{e.type}</span>
                  {e.type === 'email_sent' && e.payload?.test === true && <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900">test</span>}
                </td>
                <td>
                  {e.contact ? (
                    <Link to={`/contacts/${e.contact.id}`} className="ff-link">
                      {displayName(e.contact)}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">{'—'}</span>
                  )}
                </td>
                <td className="text-muted-foreground">{e.source}</td>
                <td className="max-w-md break-words text-xs text-muted-foreground">
                  {e.type === 'note'
                    ? String(e.payload?.text ?? '')
                    : e.type === 'email_sent' && e.payload
                      ? `${String(e.payload.templateName ?? e.payload.subject ?? '')}${e.payload.to ? ` → ${String(e.payload.to)}` : ''}${e.payload.sandbox ? ' (sandbox)' : ''}${e.payload.delivered === false ? ` · not sent: ${String(e.payload.reason ?? '')}` : ''}`
                      : e.payload
                        ? JSON.stringify(e.payload)
                        : ''}
                </td>
              </RowLink>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={data.page} pageSize={data.pageSize} total={data.total} makeHref={(page) => `/audit${toQuery({ ...query, page })}`} />
    </div>
  );
}
